import { useQuery, type UseQueryOptions } from '@tanstack/react-query';

import { QUERY_KEY } from './keys';
import { clmApiClient } from '../clients';
import type { ApiErrorResponse, Department } from '../types';
import { isApiErrorResponse } from '../utils';

type AllDepartmentsRequest = Pick<Department, 'id' | 'name'>[];

/**
 * Fetch all departments (id + name only, no pagination)
 */
const fetchAllDepartments = async (): Promise<AllDepartmentsRequest> => {
  const response = await clmApiClient.get<
    AllDepartmentsRequest,
    ApiErrorResponse
  >('departments/all');

  if (!response.data) {
    throw new Error('No data returned from all departments fetch');
  }

  if (!response.ok) {
    if (isApiErrorResponse(response.data)) {
      throw new Error(response.data.details);
    }
    throw new Error('Unknown error');
  }

  return response.data;
};

export const useAllDepartments = (
  config?: Omit<UseQueryOptions<AllDepartmentsRequest>, 'queryKey' | 'queryFn'>,
) =>
  useQuery({
    queryKey: [QUERY_KEY.DEPARTMENTS_ALL],
    queryFn: fetchAllDepartments,
    staleTime: 1000 * 60 * 5, // 5 minutes
    ...config,
  });
